import { List } from "immutable";

import {
  ENTITY_LIST_PANEL,
  GLOBAL_FILTER_PANEL,
  HARVARD_SETTINGS_PANEL,
  LOCAL_FILTER_PANEL,
  SCROLL_TO_DATE_PANEL,
  SEARCH_PANEL,
  SETTINGS_PANEL,
  TUTORIAL_PANEL
} from "../../../constants/identifiers";
import { MOBILE_LANDSCAPE_VIEW_TYPES, MOBILE_PORTRAIT_VIEW_TYPES } from "../../../constants/viewTypes";
import { createFromJS } from "../../../types";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import combineReducersWithGlobal from "../../combineReducersWithGlobal";
import { appStateSelectors } from "..";
import { actionTypes } from "../actions";
import {
  desktopOpenViews,
  excludeSearchTypes,
  expandedEntityItems,
  expandedFilterGroups,
  exposedFilterTypes,
  fontPercentage,
  leftPanelWidth,
  makeUnique,
  mobileLandscapeOpenViews,
  mobileView,
  scrollingIsLocked,
  tabletOpenViews
} from "./index.shared";

const selectedItems = (state = List(), action, globalState) => {
  switch (action.type) {
    case actionTypes.SELECT_ITEMS:
      return makeUnique(List(action.ids).filter(id => globalState.data.items.byId.has(id)));
    case actionTypes.ADD_ITEMS_TO_SELECTION:
      return makeUnique(state.push(...action.ids.filter(id => globalState.data.items.byId.has(id))));
    case actionTypes.REMOVE_ITEMS_FROM_SELECTION:
      return makeUnique(state.filter(id => action.ids.indexOf(id) === -1));
    case actionTypes.CLEAR_SELECTION:
    case actionTypes.SELECT_MARKER:
      return List();
    default:
      return state;
  }
};

const selectedMarkers = (state = List(), action) => {
  switch (action.type) {
    case actionTypes.SELECT_MARKER:
      return action.id ? List([action.id]) : List([]);
    case actionTypes.SELECT_ITEMS:
    case actionTypes.CLEAR_SELECTION:
    case actionTypes.ADD_ITEMS_TO_SELECTION:
    case actionTypes.REMOVE_ITEMS_FROM_SELECTION:
      return List();
    default:
      return state;
  }
};

const isHarvard = preprocessorQuery.isHarvard();
const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

const leftPanelSharedCases = (state, action, globalState) => {
  switch (action.type) {
    case actionTypes.CLOSE_MOBILE_INSPECTOR_PANEL:
    case actionTypes.HIDE_LEFT_PANEL:
      return null;
    case actionTypes.SET_LEFT_PANEL_MODE:
      return action.mode;
    case actionTypes.TOGGLE_ENTITY_LIST:
      return appStateSelectors.getVisibleTypeListId(globalState) === action.typeId ? null : ENTITY_LIST_PANEL;
    case actionTypes.OPEN_ENTITY_LIST:
      return ENTITY_LIST_PANEL;
    case actionTypes.TOGGLE_SEARCH_PANEL:
      return state === SEARCH_PANEL ? null : SEARCH_PANEL;
    case actionTypes.OPEN_SEARCH_PANEL:
      return SEARCH_PANEL;
    case actionTypes.TOGGLE_FILTER_PANEL: {
      let mode =
        state === GLOBAL_FILTER_PANEL || state === LOCAL_FILTER_PANEL
          ? null
          : globalState.uiState.get("showGlobalFilterPanel")
            ? GLOBAL_FILTER_PANEL
            : LOCAL_FILTER_PANEL;
      if (isHarvard) {
        mode = state === LOCAL_FILTER_PANEL ? null : LOCAL_FILTER_PANEL;
      }
      return mode;
    }
    case actionTypes.TOGGLE_GLOBAL_FILTER_PANEL:
      return state === GLOBAL_FILTER_PANEL ? null : GLOBAL_FILTER_PANEL;
    case actionTypes.TOGGLE_LOCAL_FILTER_PANEL:
      return state === LOCAL_FILTER_PANEL ? null : LOCAL_FILTER_PANEL;
    case actionTypes.OPEN_LOCAL_FILTER_PANEL:
      return LOCAL_FILTER_PANEL;

    //#!if harvard === true
    case actionTypes.TOGGLE_HARVARD_SETTINGS_PANEL:
      return state === HARVARD_SETTINGS_PANEL ? null : HARVARD_SETTINGS_PANEL;
    case actionTypes.TOGGLE_SCROLL_TO_DATE_PANEL:
      return state === SCROLL_TO_DATE_PANEL ? null : SCROLL_TO_DATE_PANEL;
      //#!endif

    default:
      return state;
  }
};

// Note: This is the "readonly" version of this list and will not affect "editing" mode
// A duplicate function exists in index.normal that you may also need to edit
const leftPanelMode = (state, action, globalState) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_DESKTOP_LEFT_PANEL ? null : newState;

  if (newState === state && isDesktop) {
    newState = leftPanelSharedCases(newState, action, globalState);
    if (isHarvard && newState === state) {
      switch (action.type) {
        case actionTypes.SWITCH_VIEW_TYPE:
          newState = action.viewType ? null : newState;
          break;
      }
    }
  }
  return newState;
};

const tabletPanelMode = (state, action, globalState) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_TABLET_LEFT_PANEL ? null : newState;

  if (newState === state && isTablet) {
    newState = leftPanelSharedCases(newState, action, globalState);
    if (newState === state) {
      switch (action.type) {
        case actionTypes.TOGGLE_SETTINGS_PANEL:
          newState = newState === SETTINGS_PANEL ? null : SETTINGS_PANEL;
          break;
        case actionTypes.SWITCH_VIEW_TYPE:
          newState = action.viewType ? null : newState;
          break;
        case actionTypes.TOGGLE_SCROLL_TO_DATE_PANEL:
          newState = newState === SCROLL_TO_DATE_PANEL ? null : SCROLL_TO_DATE_PANEL;
          break;
      }
    }
  }
  return newState;
};

const mobilePanelMode = (state, action, globalState) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_MOBILE_LEFT_PANEL ? null : newState;

  if (newState === state && isMobile) {
    newState = leftPanelSharedCases(newState, action, globalState);
    if (newState === state) {
      switch (action.type) {
        case actionTypes.TOGGLE_SETTINGS_PANEL:
          newState = newState === SETTINGS_PANEL ? null : SETTINGS_PANEL;
          break;
        case actionTypes.TOGGLE_SCROLL_TO_DATE_PANEL:
          newState = newState === SCROLL_TO_DATE_PANEL ? null : SCROLL_TO_DATE_PANEL;
          break;
        case actionTypes.SWITCH_MOBILE_PORTRAIT_VIEW_TYPE:
          newState =
            action.viewType === TUTORIAL_PANEL
              ? action.viewType
              : MOBILE_PORTRAIT_VIEW_TYPES.includes(action.viewType)
                ? null
                : state;
          break;
        case actionTypes.SWITCH_MOBILE_LANDSCAPE_VIEW_TYPE:
        case actionTypes.SWITCH_VIEW_TYPE:
          newState = MOBILE_LANDSCAPE_VIEW_TYPES.includes(action.viewType) ? null : state;
          break;
      }
    }
  }
  return newState;
};

const visibleTypeSharedCases = (state, action) => {
  switch (action.type) {
    case actionTypes.HIDE_LEFT_PANEL:
    case actionTypes.TOGGLE_SEARCH_PANEL:
    case actionTypes.OPEN_SEARCH_PANEL:
    case actionTypes.TOGGLE_FILTER_PANEL:
    case actionTypes.TOGGLE_GLOBAL_FILTER_PANEL:
    case actionTypes.TOGGLE_LOCAL_FILTER_PANEL:
    case actionTypes.OPEN_LOCAL_FILTER_PANEL:
      return null;
    case actionTypes.TOGGLE_ENTITY_LIST:
      return state === action.typeId ? null : action.typeId;
    case actionTypes.OPEN_ENTITY_LIST:
      return action.typeId;
    default:
      return state;
  }
};

// Note: This is the "readonly" version of this list and will not affect "editing" mode
// A duplicate function exists in index.normal that you may also need to edit
const visibleTypeList = (state, action) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_DESKTOP_LEFT_PANEL ? null : newState;
  if (newState === state && isDesktop) {
    newState = visibleTypeSharedCases(state, action);
  }
  return newState;
};

const tabletVisibleTypeList = (state, action) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_TABLET_LEFT_PANEL ? null : newState;

  if (newState === state && isTablet) {
    newState = visibleTypeSharedCases(state, action);
  }
  return newState;
};

const mobileVisibleTypeList = (state, action) => {
  let newState = state;
  newState = action.type === actionTypes.HIDE_MOBILE_LEFT_PANEL ? null : newState;

  if (newState === state && isMobile) {
    newState = visibleTypeSharedCases(state, action);
    switch (action.type) {
      case actionTypes.SWITCH_MOBILE_PORTRAIT_VIEW_TYPE:
        newState = MOBILE_PORTRAIT_VIEW_TYPES.includes(action.viewType) ? null : state;
        break;
      case actionTypes.SWITCH_MOBILE_LANDSCAPE_VIEW_TYPE:
      case actionTypes.SWITCH_VIEW_TYPE:
        newState = MOBILE_LANDSCAPE_VIEW_TYPES.includes(action.viewType) ? null : state;
        break;
    }
  }
  return newState;
};

const reducerReadonly = combineReducersWithGlobal(createFromJS.AppStateRecord({}), {
  desktopOpenViews,
  tabletOpenViews,
  mobileLandscapeOpenViews,
  scrollingIsLocked,
  leftPanelWidth,
  mobileView,
  expandedEntityItems,
  expandedFilterGroups,
  excludeSearchTypes,
  exposedFilterTypes,

  selectedItems,
  selectedMarkers,
  leftPanelMode,
  tabletPanelMode,
  mobilePanelMode,
  visibleTypeList,
  tabletVisibleTypeList,
  mobileVisibleTypeList,

  fontPercentage
});

export default reducerReadonly;
